/**
 * Sorting a table on its columns, the one way for every table of the
 * dashboard: a click on a column's header (`<SortableHeadCell>` in
 * `@/components/data-table`) sorts on it, a second click turns the order
 * round. Words compare the dashboard's way (case and accents aside, "2" before
 * "10"), numbers and times by value, a row without a value goes last either
 * way, and rows with the same value keep their order.
 *
 * A table sorted in the browser:
 *
 *     const sort = useTableSort({
 *       columns: { name: "asc", calls: "desc", last: "desc" },
 *       initial: { key: "calls", dir: "desc" },
 *     });
 *     const shown = sortRows(rows, sort.sort, (row, key) =>
 *       key === "name" ? row.name : key === "calls" ? row.calls : sortTime(row.lastAt),
 *     );
 *     <SortableHeadCell {...sort.head("name")}>Naam</SortableHeadCell>
 *
 * A page that keeps its filters in the URL keeps the sort there too, with
 * `useUrlTableSort` (`?sort=calls&dir=asc`, left out at the table's first
 * sort). A table the server sorts (a paged one) passes `sort.sort` to its
 * query instead of calling `sortRows`, and goes back to its first page in
 * `onChange`.
 */

import { useState } from "react";
import { useSearchParams } from "react-router";

import { LOCALES } from "@/lib/format";
import { getLanguage, type Language } from "@/lib/i18n/language";

export type SortDir = "asc" | "desc";

/** A table's sort: the column it sorts on and the order. */
export interface TableSort<K extends string> {
  key: K;
  dir: SortDir;
}

/**
 * What a row sorts on in a column: a text (a name, a title), a number (a
 * count, a duration, a time as `sortTime`), or nothing, which sorts last in
 * both orders.
 */
export type SortValue = string | number | null | undefined;

/**
 * A table's sortable columns, each with the order a first click on it sorts
 * in: `"asc"` for words (from A) and `"desc"` for numbers and times (the most
 * and the newest first).
 */
export type SortColumns<K extends string> = Readonly<Record<K, SortDir>>;

/** A column header's `aria-sort`: only the sorted column carries one. */
export type AriaSort = "ascending" | "descending" | undefined;

/**
 * The sort after a click on `key`'s header: the sorted column turns its order
 * round, another column starts in its own first order (`columns`).
 */
export function nextSort<K extends string>(
  current: TableSort<K> | null,
  key: K,
  columns: SortColumns<K>,
): TableSort<K> {
  if (current?.key === key) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: columns[key] };
}

/** The `aria-sort` of a column's header. */
export function ariaSort<K extends string>(sort: TableSort<K> | null, key: K): AriaSort {
  if (sort?.key !== key) {
    return undefined;
  }
  return sort.dir === "asc" ? "ascending" : "descending";
}

/** A time the API sends (ISO) as a number to sort on; null when missing or invalid. */
export function sortTime(moment: string | null | undefined): number | null {
  if (!moment) {
    return null;
  }
  const time = Date.parse(moment);
  return Number.isNaN(time) ? null : time;
}

/**
 * The rows in the sort's order, by each row's value for the sorted column
 * (`value`), or as they came without a sort. Words compare in the dashboard's
 * language, ignoring case and accents, with numbers in them by value; a row
 * without a value goes last in both orders; rows with the same value keep the
 * order they came in.
 */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  sort: TableSort<K> | null,
  value: (row: T, key: K) => SortValue,
  language: Language = getLanguage(),
): T[] {
  if (sort === null) {
    return [...rows];
  }
  const collator = new Intl.Collator(LOCALES[language], { sensitivity: "base", numeric: true });
  const sign = sort.dir === "asc" ? 1 : -1;
  const keyed = rows.map((row) => ({ row, at: value(row, sort.key) }));
  keyed.sort((a, b) => {
    const x = a.at;
    const y = b.at;
    const xMissing = x === null || x === undefined || x === "";
    const yMissing = y === null || y === undefined || y === "";
    if (xMissing || yMissing) {
      return xMissing === yMissing ? 0 : xMissing ? 1 : -1;
    }
    const order =
      typeof x === "number" && typeof y === "number"
        ? x - y
        : collator.compare(String(x), String(y));
    return sign * order;
  });
  return keyed.map((entry) => entry.row);
}

/** At most this many values in the order a server sort follows: the API's own limit. */
export const MAX_SORT_ORDER = 200;

/**
 * A coded column's values in the order their words read in `language`, for a
 * table the server sorts on that column (`sortOrder`): the server knows only
 * the codes (`odoo_slow`), the table shows the words ("Odoo te traag").
 * Compared as `sortRows` compares words, codes with the same words by their
 * code; each code once, at most `MAX_SORT_ORDER`, so a code past them sorts
 * after the listed ones on the server. `label` is the function the table's
 * cells use, so the order is the order on screen.
 *
 *     sortOrder: sort.sort?.key === "action" ? labelOrder(actions, activityActionLabel, language) : undefined
 */
export function labelOrder(
  values: Iterable<string>,
  label: (value: string) => string,
  language: Language = getLanguage(),
): string[] {
  const codes = [...new Set(values)].sort();
  return sortRows(codes, { key: "label", dir: "asc" }, label, language).slice(0, MAX_SORT_ORDER);
}

/** The search parameters a sort lives in. */
export interface SortParamNames {
  sort: string;
  dir: string;
}

/** `?sort=calls&dir=asc`: the names of a page with one sorted table. */
export const SORT_PARAMS: SortParamNames = { sort: "sort", dir: "dir" };

/**
 * The sort in a page's search parameters; null when there is none or it names
 * a column the table does not sort on. A missing or unknown order takes the
 * column's first order.
 */
export function parseSort<K extends string>(
  params: URLSearchParams,
  columns: SortColumns<K>,
  names: SortParamNames = SORT_PARAMS,
): TableSort<K> | null {
  const key = params.get(names.sort);
  if (key === null || !Object.hasOwn(columns, key)) {
    return null;
  }
  const column = key as K;
  const dir = params.get(names.dir);
  return { key: column, dir: dir === "asc" || dir === "desc" ? dir : columns[column] };
}

/**
 * The page's search parameters with a sort: set, or left out when it is the
 * table's first sort, so a link stays short. Every other parameter stays.
 */
export function withSortParams<K extends string>(
  params: URLSearchParams,
  sort: TableSort<K> | null,
  initial: TableSort<K> | null,
  names: SortParamNames = SORT_PARAMS,
): URLSearchParams {
  const next = new URLSearchParams(params);
  const isInitial = sort?.key === initial?.key && sort?.dir === initial?.dir;
  if (sort === null || isInitial) {
    next.delete(names.sort);
    next.delete(names.dir);
  } else {
    next.set(names.sort, sort.key);
    next.set(names.dir, sort.dir);
  }
  return next;
}

export interface TableSortOptions<K extends string> {
  /** The sortable columns, each with the order a first click sorts in. */
  columns: SortColumns<K>;
  /** The sort the table opens with; null for the order the rows came in. */
  initial: TableSort<NoInfer<K>> | null;
  /** After every change, such as a paged table's way back to its first page. */
  onChange?: (sort: TableSort<K>) => void;
}

/** A table's sort, and what its headers need. */
export interface TableSortState<K extends string> {
  /** The sort now; null while the rows keep the order they came in. */
  sort: TableSort<K> | null;
  /** A click on a column's header. */
  onSort: (key: K) => void;
  /** The props of a column's `<SortableHeadCell>`: its `aria-sort` and its click. */
  head: (key: K) => { sort: AriaSort; onSort: () => void };
}

function sortState<K extends string>(
  sort: TableSort<K> | null,
  change: (sort: TableSort<K>) => void,
  columns: SortColumns<K>,
): TableSortState<K> {
  const onSort = (key: K) => change(nextSort(sort, key, columns));
  return {
    sort,
    onSort,
    head: (key) => ({ sort: ariaSort(sort, key), onSort: () => onSort(key) }),
  };
}

/** A table's sort, kept while the page is open. */
export function useTableSort<K extends string>({
  columns,
  initial,
  onChange,
}: TableSortOptions<K>): TableSortState<K> {
  const [sort, setSort] = useState<TableSort<K> | null>(initial);
  return sortState(
    sort,
    (next) => {
      setSort(next);
      onChange?.(next);
    },
    columns,
  );
}

export interface UrlTableSortOptions<K extends string> extends TableSortOptions<K> {
  /** The search parameters to use, for a page with several sorted tables; `sort` and `dir` by default. */
  names?: SortParamNames;
}

/**
 * A table's sort kept in the page's URL (`?sort=calls&dir=asc`), for a page
 * that keeps its filters there, so a reload or a shared link keeps the order.
 * It replaces the history entry, as the filters do, and leaves every other
 * parameter as it is. A page that writes all its parameters at once keeps the
 * sort's with `withSortParams`.
 */
export function useUrlTableSort<K extends string>({
  columns,
  initial,
  onChange,
  names = SORT_PARAMS,
}: UrlTableSortOptions<K>): TableSortState<K> {
  const [params, setParams] = useSearchParams();
  const sort = parseSort(params, columns, names) ?? initial;
  return sortState(
    sort,
    (next) => {
      setParams((current) => withSortParams(current, next, initial, names), { replace: true });
      onChange?.(next);
    },
    columns,
  );
}
